import { supabase } from '../utils/supabase';

/**
 * Guest Service
 * Handles guest list, RSVP, and invitation operations
 */
class GuestService {
    /**
     * Get all guests for a user
     */
    async getUserGuests(userId) {
        try {
            const { data, error } = await supabase
                .from('guests')
                .select(`
          *,
          rsvp:rsvps(*),
          invitation:invitations(*)
        `)
                .eq('user_id', userId)
                .order('full_name');

            if (error) throw error;

            return {
                success: true,
                data: data || []
            };
        } catch (error) {
            console.error('Error fetching guests:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Add a new guest
     */
    async addGuest(userId, guestData) {
        try {
            const { data, error } = await supabase
                .from('guests')
                .insert({
                    user_id: userId,
                    full_name: guestData.name,
                    email: guestData.contact,
                    phone: guestData.phone,
                    guest_type: guestData.group?.toLowerCase() || 'other',
                    side: guestData.side?.toLowerCase() || 'both',
                    plus_one_allowed: guestData.plusOne || false,
                    dietary_restrictions: guestData.dietaryRestrictions,
                    notes: guestData.notes,
                    invited: false
                })
                .select(`
          *,
          rsvp:rsvps(*),
          invitation:invitations(*)
        `)
                .single();

            if (error) throw error;

            return {
                success: true,
                data
            };
        } catch (error) {
            console.error('Error adding guest:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Update a guest
     */
    async updateGuest(guestId, updates) {
        try {
            const { data, error } = await supabase
                .from('guests')
                .update({
                    full_name: updates.name,
                    email: updates.contact,
                    phone: updates.phone,
                    guest_type: updates.group?.toLowerCase(),
                    side: updates.side?.toLowerCase(),
                    plus_one_allowed: updates.plusOne,
                    dietary_restrictions: updates.dietaryRestrictions,
                    notes: updates.notes
                })
                .eq('id', guestId)
                .select(`
          *,
          rsvp:rsvps(*),
          invitation:invitations(*)
        `)
                .single();

            if (error) throw error;

            return {
                success: true,
                data
            };
        } catch (error) {
            console.error('Error updating guest:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Delete a guest
     */
    async deleteGuest(guestId) {
        try {
            const { error } = await supabase
                .from('guests')
                .delete()
                .eq('id', guestId);

            if (error) throw error;

            return {
                success: true
            };
        } catch (error) {
            console.error('Error deleting guest:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Update or create RSVP for a guest
     */
    async updateRSVP(guestId, rsvpData) {
        try {
            // First check if RSVP exists
            const { data: existing } = await supabase
                .from('rsvps')
                .select('id')
                .eq('guest_id', guestId)
                .single();

            let result;

            if (existing) {
                // Update existing RSVP
                result = await supabase
                    .from('rsvps')
                    .update({
                        status: rsvpData.status?.toLowerCase() || 'pending',
                        guests_count: rsvpData.guestsCount || 1,
                        meal_preference: rsvpData.mealPreference,
                        dietary_restrictions: rsvpData.dietaryRestrictions,
                        special_requests: rsvpData.specialRequests,
                        notes: rsvpData.notes,
                        responded_at: rsvpData.status !== 'pending' ? new Date().toISOString() : null
                    })
                    .eq('guest_id', guestId)
                    .select()
                    .single();
            } else {
                // Create new RSVP
                result = await supabase
                    .from('rsvps')
                    .insert({
                        guest_id: guestId,
                        status: rsvpData.status?.toLowerCase() || 'pending',
                        guests_count: rsvpData.guestsCount || 1,
                        meal_preference: rsvpData.mealPreference,
                        dietary_restrictions: rsvpData.dietaryRestrictions,
                        special_requests: rsvpData.specialRequests,
                        notes: rsvpData.notes,
                        responded_at: rsvpData.status !== 'pending' ? new Date().toISOString() : null
                    })
                    .select()
                    .single();
            }

            if (result.error) throw result.error;

            return {
                success: true,
                data: result.data
            };
        } catch (error) {
            console.error('Error updating RSVP:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Get RSVP statistics for a user
     */
    async getRSVPStats(userId) {
        try {
            // Use the database function
            const { data, error } = await supabase
                .rpc('get_user_rsvp_stats', { user_uuid: userId });

            if (error) throw error;

            const stats = data && data.length > 0 ? data[0] : {
                total_guests: 0,
                invited: 0,
                confirmed: 0,
                declined: 0,
                pending: 0,
                total_attending: 0
            };

            return {
                success: true,
                data: {
                    totalInvited: parseInt(stats.total_guests) || 0,
                    totalResponded: parseInt(stats.confirmed) + parseInt(stats.declined) || 0,
                    confirmed: parseInt(stats.confirmed) || 0,
                    declined: parseInt(stats.declined) || 0,
                    pending: parseInt(stats.pending) || 0,
                    totalAttending: parseInt(stats.total_attending) || 0
                }
            };
        } catch (error) {
            console.error('Error fetching RSVP stats:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Get recent RSVP responses
     */
    async getRecentRSVPs(userId, limit = 5) {
        try {
            const { data, error } = await supabase
                .from('rsvps')
                .select(`
          *,
          guest:guests!inner(
            *
          )
        `)
                .eq('guest.user_id', userId)
                .not('responded_at', 'is', null)
                .order('responded_at', { ascending: false })
                .limit(limit);

            if (error) throw error;

            // Format the data
            const formatted = (data || []).map(rsvp => ({
                id: rsvp.id,
                name: rsvp.guest.full_name,
                status: rsvp.status.charAt(0).toUpperCase() + rsvp.status.slice(1),
                respondedDate: rsvp.responded_at,
                guestsCount: rsvp.guests_count,
                email: rsvp.guest.email,
                phone: rsvp.guest.phone
            }));

            return {
                success: true,
                data: formatted
            };
        } catch (error) {
            console.error('Error fetching recent RSVPs:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Get guests pending follow-up
     */
    async getPendingFollowUps(userId) {
        try {
            const { data, error } = await supabase
                .from('guests')
                .select(`
          *,
          rsvp:rsvps(*),
          invitation:invitations(*)
        `)
                .eq('user_id', userId)
                .eq('invited', true)
                .order('created_at', { ascending: true });

            if (error) throw error;

            // Filter for pending RSVPs (no RSVP or status is pending)
            const pendingGuests = (data || []).filter(guest => {
                return !guest.rsvp || guest.rsvp.length === 0 || guest.rsvp[0]?.status === 'pending';
            });

            // Format the data with days calculation
            const formatted = pendingGuests.map(guest => {
                const invitedDate = guest.invitation && guest.invitation.length > 0
                    ? new Date(guest.invitation[0].sent_at)
                    : new Date(guest.created_at);
                const daysSinceInvite = Math.floor((Date.now() - invitedDate.getTime()) / (1000 * 60 * 60 * 24));

                return {
                    id: guest.id,
                    name: guest.full_name,
                    email: guest.email,
                    phone: guest.phone,
                    daysSinceInvite,
                    lastReminder: guest.invitation && guest.invitation.length > 0
                        ? guest.invitation[0].last_reminder_sent_at
                        : null,
                    reminderCount: guest.invitation && guest.invitation.length > 0
                        ? guest.invitation[0].reminder_count
                        : 0
                };
            });

            return {
                success: true,
                data: formatted
            };
        } catch (error) {
            console.error('Error fetching pending follow-ups:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Send invitation to a guest
     */
    async sendInvitation(guestId, method = 'email', message = '') {
        try {
            // Mark guest as invited
            const { error: guestError } = await supabase
                .from('guests')
                .update({ invited: true })
                .eq('id', guestId);

            if (guestError) throw guestError;

            // Check if invitation already exists
            const { data: existing } = await supabase
                .from('invitations')
                .select('id')
                .eq('guest_id', guestId)
                .single();

            if (existing) {
                // Update existing invitation
                const { error } = await supabase
                    .from('invitations')
                    .update({
                        sent_at: new Date().toISOString(),
                        sent_via: method,
                        invitation_message: message
                    })
                    .eq('id', existing.id);

                if (error) throw error;
            } else {
                // Create new invitation record
                const { error } = await supabase
                    .from('invitations')
                    .insert({
                        guest_id: guestId,
                        sent_at: new Date().toISOString(),
                        sent_via: method,
                        invitation_message: message
                    });

                if (error) throw error;
            }

            // TODO: Actually send email/SMS via email service
            // await emailService.sendRSVPInvitation(guestEmail, inviteLink, message);

            return {
                success: true,
                message: `Invitation sent via ${method}`
            };
        } catch (error) {
            console.error('Error sending invitation:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Send bulk invitations
     */
    async sendBulkInvitations(guestIds, method = 'email', message = '') {
        try {
            const results = await Promise.all(
                guestIds.map(id => this.sendInvitation(id, method, message))
            );

            const successful = results.filter(r => r.success).length;
            const failed = results.filter(r => !r.success).length;

            return {
                success: true,
                data: {
                    successful,
                    failed,
                    total: guestIds.length
                }
            };
        } catch (error) {
            console.error('Error sending bulk invitations:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Send reminder to a guest
     */
    async sendReminder(guestId) {
        try {
            // Increment reminder count
            const { error } = await supabase
                .rpc('increment_reminder_count', { guest_uuid: guestId });

            if (error) throw error;

            // TODO: Actually send reminder email/SMS
            // await emailService.sendRSVPReminder(guestEmail);

            return {
                success: true,
                message: 'Reminder sent successfully'
            };
        } catch (error) {
            console.error('Error sending reminder:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Send bulk reminders
     */
    async sendBulkReminders(guestIds) {
        try {
            const results = await Promise.all(
                guestIds.map(id => this.sendReminder(id))
            );

            const successful = results.filter(r => r.success).length;
            const failed = results.filter(r => !r.success).length;

            return {
                success: true,
                data: {
                    successful,
                    failed,
                    total: guestIds.length
                }
            };
        } catch (error) {
            console.error('Error sending bulk reminders:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Export guest list to CSV
     */
    async exportCSV(userId) {
        try {
            const { data: guests } = await this.getUserGuests(userId);

            if (!guests || guests.length === 0) {
                return {
                    success: false,
                    error: 'No guests to export'
                };
            }

            // Create CSV header
            const headers = [
                'Name',
                'Email',
                'Phone',
                'Type',
                'Side',
                'Plus One',
                'RSVP Status',
                'Guests Count',
                'Meal Preference',
                'Dietary Restrictions',
                'Invited',
                'Responded Date'
            ];

            // Create CSV rows
            const rows = guests.map(guest => [
                guest.full_name || '',
                guest.email || '',
                guest.phone || '',
                guest.guest_type || '',
                guest.side || '',
                guest.plus_one_allowed ? 'Yes' : 'No',
                guest.rsvp && guest.rsvp.length > 0 ? guest.rsvp[0].status : 'pending',
                guest.rsvp && guest.rsvp.length > 0 ? guest.rsvp[0].guests_count : 1,
                guest.rsvp && guest.rsvp.length > 0 ? guest.rsvp[0].meal_preference || '' : '',
                guest.rsvp && guest.rsvp.length > 0 ? guest.rsvp[0].dietary_restrictions || '' : '',
                guest.invited ? 'Yes' : 'No',
                guest.rsvp && guest.rsvp.length > 0 && guest.rsvp[0].responded_at
                    ? new Date(guest.rsvp[0].responded_at).toLocaleDateString()
                    : ''
            ]);

            // Combine headers and rows
            const csvContent = [
                headers.join(','),
                ...rows.map(row => row.map(cell => {
                    // Escape commas and quotes in cell content
                    const escaped = String(cell).replace(/"/g, '""');
                    return escaped.includes(',') ? `"${escaped}"` : escaped;
                }).join(','))
            ].join('\n');

            return {
                success: true,
                data: csvContent
            };
        } catch (error) {
            console.error('Error exporting CSV:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Download CSV file
     */
    downloadCSV(csvContent, filename = 'guest-list.csv') {
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');

        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    }
}

export default new GuestService();
